import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import type { ILeaderboardEntry } from '@/interfaces';
import { PartnersSectionTitle } from '../TrackingInsights.styles';
import { useImageFallback } from '../useImageFallback.hook';
import {
  LogoCard,
  LogoImage,
  LogoTrack,
  LogoViewport,
  PARTNER_CARD_GAP,
  PARTNER_CARD_WIDTH,
  PARTNER_MIN_LAP_WIDTH,
  PartnersContainer,
  PartnersSection,
} from './PartnerLogos.styles';

const FALLBACK_LOGO = '/images/default-carrier-logo.svg';

interface PartnerCardProps {
  partner: ILeaderboardEntry;
  /** Any repeat of a brand in the marquee: visible, but announced once is enough. */
  isDuplicate?: boolean;
}

/**
 * How many times the list repeats within one lap.
 *
 * The loop shifts the track by exactly one lap, so a lap narrower than the viewport leaves a
 * blank stretch on the right before the reset — a page with a handful of brands would show one.
 * Repeating the list until a lap spans the widest window the strip is drawn for closes that gap.
 */
const getRepeatsPerLap = (logoCount: number) =>
  Math.max(1, Math.ceil(PARTNER_MIN_LAP_WIDTH / (logoCount * (PARTNER_CARD_WIDTH + PARTNER_CARD_GAP))));

const PartnerCard: React.FC<PartnerCardProps> = ({ partner, isDuplicate }) => {
  const { ref, hasFailed, onError } = useImageFallback(partner.logoUrl ?? '');

  return (
    <LogoCard aria-hidden={isDuplicate || undefined}>
      <LogoImage
        ref={ref}
        src={hasFailed || !partner.logoUrl ? FALLBACK_LOGO : partner.logoUrl}
        alt={partner.name}
        loading='lazy'
        onError={onError}
      />
    </LogoCard>
  );
};

export interface PartnerLogosProps {
  /** The page's top carriers and merchants, already de-duplicated (see `getPartnerLogos`). */
  logos: ILeaderboardEntry[];
}

/**
 * Partner logo strip (Figma 20369:4757) — the band under the hero on the country and region
 * templates. It shows the brands the page itself ranks below (top carriers + top merchants), so
 * the strip is a preview of the page's own data rather than a fixed set of brands.
 *
 * The strip travels sideways continuously. One lap (the list, repeated until it is wider than the
 * viewport) is rendered twice: the second lap is what makes the loop seamless. Every repeat is
 * hidden from assistive technology so each brand is announced once.
 */
const PartnerLogos: React.FC<PartnerLogosProps> = ({ logos }) => {
  const { t } = useGlobalState();

  if (!logos.length) return null;

  const repeatsPerLap = getRepeatsPerLap(logos.length);
  // Two laps back to back; only the first copy of the list is exposed to assistive technology.
  const track = Array.from({ length: repeatsPerLap * 2 }, (_, copy) =>
    logos.map((partner) => ({ partner, copy })),
  ).flat();

  return (
    <PartnersSection>
      <PartnersContainer>
        <PartnersSectionTitle type='h2'>{t(MasterTranslation.PartnerLogosTitle)}</PartnersSectionTitle>
      </PartnersContainer>

      {/* Outside the container: the strip runs edge to edge, only the title sits on the column. */}
      <LogoViewport>
        <LogoTrack $cardCount={logos.length * repeatsPerLap}>
          {track.map(({ partner, copy }) => (
            <PartnerCard key={`${copy}-${partner.name}`} partner={partner} isDuplicate={copy > 0} />
          ))}
        </LogoTrack>
      </LogoViewport>
    </PartnersSection>
  );
};

export default PartnerLogos;
